import React, { useState, useEffect, useRef } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { getUserProfile, updateUserProfile, getCategories, getCountries } from '../../services/api';
import { useAuth } from '../auth/AuthContext';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { motion } from 'framer-motion';
import { Upload, AlertTriangle, Save, Loader2 } from 'lucide-react';
import styles from './EditProfile.module.scss';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import Avatar from '../common/Avatar';

const CustomErrorMessage = ({ name }) => (
  <ErrorMessage
    name={name}
    render={msg => (
      <div className={styles.errorMessage}>
        <AlertTriangle size={14} /> {msg}
      </div>
    )}
  />
);

const EditProfile = () => {
  const { t } = useTranslation(['profile', 'common']);
  const navigate = useNavigate();
  const { refreshUser, currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [formError, setFormError] = useState(null);
  const [userType, setUserType] = useState('');
  const [initialValues, setInitialValues] = useState({});
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const formRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const profileResponse = await getUserProfile();
        const userData = profileResponse.data;
        setUserType(userData.user_type);

        const baseValues = {
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          city: userData.city || '',
          country: userData.country || '',
          phone_number: userData.phone_number || '',
        };

        if (userData.display_image_url || userData.profile_picture_url || userData.profile_picture || userData.creator_profile?.ig_profile_picture_url || userData.business_profile?.ig_profile_picture_url) {
          setProfilePicturePreview(userData.display_image_url || userData.profile_picture_url || userData.profile_picture || userData.creator_profile?.ig_profile_picture_url || userData.business_profile?.ig_profile_picture_url);
        }

        if (userData.user_type === 'creator') {
          setInitialValues({
            ...baseValues,
            ig_account: userData.creator_profile?.ig_account || '',
            ig_followers: userData.creator_profile?.ig_followers || '',
            description: userData.creator_profile?.description || userData.creator_profile?.ig_biography || '',
            website: userData.creator_profile?.website || userData.creator_profile?.ig_website || '',
            content_categories: userData.creator_profile?.content_categories?.map(cat => cat.id.toString()) || [],
          });

          const categoriesResponse = await getCategories('creator');
          setCategories(categoriesResponse.data);
        } else {
          setInitialValues({
            ...baseValues,
            company_name: userData.business_profile?.company_name || '',
            ruc_tax_id: userData.business_profile?.ruc_tax_id || '',
            role: userData.business_profile?.role || '',
            instagram_account: userData.business_profile?.instagram_account || '',
            description: userData.business_profile?.description || '',
            website: userData.business_profile?.website || userData.business_profile?.ig_website || '',
            business_categories: userData.business_profile?.business_categories?.map(cat => cat.id.toString()) || [],
          });

          const categoriesResponse = await getCategories('business');
          setCategories(categoriesResponse.data);
        }

        const countriesResponse = await getCountries();
        setCountries(countriesResponse.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setFormError(t('view.error_generic'));
        setLoading(false);
      }
    };

    fetchData();
  }, [refreshUser, navigate, t]);

  const CreatorProfileSchema = Yup.object().shape({
    first_name: Yup.string().required(t('form.errors.required')),
    last_name: Yup.string().required(t('form.errors.required')),
    city: Yup.string().required(t('form.errors.required')),
    country: Yup.number().required(t('form.errors.required')),
    phone_number: Yup.string().required(t('form.errors.required')),
    ig_account: Yup.string().matches(/^@?([a-zA-Z0-9_]){1,30}$/, t('form.errors.invalid_ig')),
    description: Yup.string().max(150, t('form.errors.max_length', { count: 150 })),
  });

  const BusinessProfileSchema = Yup.object().shape({
    first_name: Yup.string().required(t('form.errors.required')),
    last_name: Yup.string().required(t('form.errors.required')),
    city: Yup.string().required(t('form.errors.required')),
    country: Yup.number().required(t('form.errors.required')),
    phone_number: Yup.string().required(t('form.errors.required')),
    company_name: Yup.string().required(t('form.errors.required')),
    description: Yup.string().max(150, t('form.errors.max_length', { count: 150 })),
  });

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    try {
      setFormError(null);
      const formData = new FormData();
      
      // Basic Fields
      Object.keys(values).forEach(key => {
        if (key === 'profile_picture') return;
        if (Array.isArray(values[key])) {
          values[key].forEach(val => formData.append(key, val));
        } else if (values[key] !== null && values[key] !== undefined) {
          formData.append(key, values[key]);
        }
      });

      if (profilePicture) formData.append('profile_picture', profilePicture);

      await updateUserProfile(formData);
      const updatedUser = await refreshUser();
      const targetUsername = updatedUser?.username || currentUser?.username;
      navigate(`/profile/${targetUsername}`);
    } catch (error) {
      console.error('Error updating profile:', error);
      if (error.response?.data && typeof error.response.data === 'object') {
        const backendErrors = error.response.data;
        const fieldErrors = {};
        Object.entries(backendErrors).forEach(([key, value]) => {
          if (key !== 'detail' && key !== 'non_field_errors') {
            fieldErrors[key] = Array.isArray(value) ? value.join(', ') : String(value);
          }
        });
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
        }
        const detail = backendErrors.detail || backendErrors.non_field_errors;
        setFormError(detail ? (Array.isArray(detail) ? detail.join(', ') : String(detail)) : t('form.errors.save_error'));
      } else {
        setFormError(t('form.errors.save_error'));
      }
      setSubmitting(false);
    }
  };

  const handleFileChange = (event, setFieldValue) => {
    const file = event.currentTarget.files[0];
    if (file) {
      setProfilePicture(file);
      setFieldValue('profile_picture', file);
      const reader = new FileReader();
      reader.onloadend = () => setProfilePicturePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  if (loading) return (
    <div className={styles.editProfilePage}>
        <div className="loading-spinner" />
    </div>
  );

  return (
    <div className={styles.editProfilePage}>
      <div className={styles.editProfileContainer}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className={styles.editProfileHeader}>
            <h1>{t('view.edit_profile')}</h1>
            <p>{t('view.edit_profile_subtitle')}</p>
          </div>

          <Formik
            innerRef={formRef}
            initialValues={initialValues}
            validationSchema={userType === 'creator' ? CreatorProfileSchema : BusinessProfileSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, errors, touched, isSubmitting, setFieldValue, isValid }) => (
              <Form className={styles.editForm}>
                
                {/* ── SECTION: AVATAR ── */}
                <div className={styles.formSection}>
                  <div className={styles.sectionHeader}>
                    <h2>{t('sections.identity')}</h2>
                  </div>
                  
                  <div className={styles.avatarUploadSection}>
                    <div className={styles.avatarContainer}>
                      <Avatar
                        size="lg"
                        src={profilePicturePreview}
                        fallbackSrc={userType === 'creator' ? currentUser?.creator_profile?.ig_profile_picture_url : currentUser?.business_profile?.ig_profile_picture_url}
                        name={initialValues.company_name || [initialValues.first_name, initialValues.last_name].filter(Boolean).join(' ') || currentUser?.username}
                        alt="Profile preview"
                        className={styles.currentAvatar}
                      />
                    </div>
                    <label htmlFor="profile_picture" className={styles.changePhotoBtn}>
                      <Upload size={16} /> {t('labels.change_photo')}
                    </label>
                    <input id="profile_picture" name="profile_picture" type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleFileChange(e, setFieldValue)} />
                  </div>

                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label>{t('labels.first_name')} <span className={styles.required}>*</span></label>
                      <Field name="first_name" className={clsx(errors.first_name && touched.first_name && styles.fieldError)} />
                      <CustomErrorMessage name="first_name" />
                    </div>
                    <div className={styles.formGroup}>
                      <label>{t('labels.last_name')} <span className={styles.required}>*</span></label>
                      <Field name="last_name" className={clsx(errors.last_name && touched.last_name && styles.fieldError)} />
                      <CustomErrorMessage name="last_name" />
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label>{t('labels.phone')} <span className={styles.required}>*</span></label>
                    <PhoneInput
                      country={'pa'}
                      value={values.phone_number}
                      onChange={(value) => setFieldValue('phone_number', value)}
                      containerClass={styles.phoneInputContainer}
                      inputClass={clsx(styles.phoneInput, errors.phone_number && touched.phone_number && styles.fieldError)}
                    />
                    <CustomErrorMessage name="phone_number" />
                  </div>
                </div>

                {/* ── SECTION: LOCATION ── */}
                <div className={styles.formSection}>
                  <div className={styles.sectionHeader}>
                    <h2>{t('labels.location')}</h2>
                  </div>
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label>{t('labels.city')} <span className={styles.required}>*</span></label>
                      <Field name="city" />
                      <CustomErrorMessage name="city" />
                    </div>
                    <div className={styles.formGroup}>
                      <label>{t('labels.country')} <span className={styles.required}>*</span></label>
                      <Field as="select" name="country">
                        <option value="">{t('labels.select_country')}</option>
                        {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </Field>
                      <CustomErrorMessage name="country" />
                    </div>
                  </div>
                </div>

                {/* ── SECTION: SPECIFIC ── */}
                <div className={styles.formSection}>
                  <div className={styles.sectionHeader}>
                    <h2>{userType === 'creator' ? t('sections.creator_details') : t('sections.business_details')}</h2>
                  </div>

                  {userType === 'creator' ? (
                    <>
                      <div className={styles.formGroup}>
                        <label>{t('labels.instagram')} (handle)</label>
                        <Field name="ig_account" placeholder="@username" disabled />
                        <div className={styles.fieldHint}>
                          {t('labels.ig_synced_auto')}
                        </div>
                      </div>
                      <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                          <label>{t('labels.followers_ig')}</label>
                          <Field type="number" name="ig_followers" disabled />
                        </div>
                        <div className={styles.formGroup}>
                          <label>{t('labels.website')}</label>
                          <Field name="website" placeholder="https://..." />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={styles.formGroup}>
                        <label>{t('labels.company_name')} <span className={styles.required}>*</span></label>
                        <Field name="company_name" />
                        <CustomErrorMessage name="company_name" />
                      </div>
                      <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                          <label>{t('labels.role')}</label>
                          <Field name="role" />
                        </div>
                        <div className={styles.formGroup}>
                          <label>{t('labels.website')}</label>
                          <Field name="website" placeholder="https://..." />
                        </div>
                      </div>
                    </>
                  )}

                  <div className={styles.formGroup}>
                    <label>{t('labels.bio')}</label>
                    <Field as="textarea" name="description" placeholder={t('labels.bio_placeholder')} />
                    <div className={styles.charCounter} data-warning={values.description?.length > 130 ? 'true' : undefined} data-error={values.description?.length > 150 ? 'true' : undefined}>
                      {values.description?.length || 0} / 150
                    </div>
                  </div>
                </div>

                <div className={styles.formSection}>
                  <div className={styles.sectionHeader}>
                    <h2>{t('labels.categories')}</h2>
                  </div>
                  <div className={styles.categoriesContainer}>
                    {categories.map(cat => (
                      <div key={cat.id} className={styles.formCheck}>
                        <Field 
                          type="checkbox" 
                          name={userType === 'creator' ? 'content_categories' : 'business_categories'} 
                          value={cat.id.toString()} 
                          id={`cat-${cat.id}`} 
                        />
                        <label htmlFor={`cat-${cat.id}`}>{cat.name}</label>
                      </div>
                    ))}
                  </div>
                </div>

                {formError && <div className={styles.errorMessage}><AlertTriangle size={16} /> {formError}</div>}

                <div className={styles.formActions}>
                  <button type="button" className={styles.btnCancel} onClick={() => navigate(-1)} disabled={isSubmitting}>
                    {t('common:buttons.cancel')}
                  </button>
                  <button type="submit" className={styles.btnPrimary} disabled={isSubmitting || !isValid}>
                    {isSubmitting ? <Loader2 className="spin" size={18} /> : <Save size={18} />}
                    {isSubmitting ? t('common:buttons.saving') : t('common:buttons.save')}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </motion.div>
      </div>
    </div>
  );
};

export default EditProfile;
